import {
  HINTS_CLOSED_KEY,
  HINTS_EVENT,
  SHOW_HINTS_KEY,
  readHintSettings,
} from '../renderer/document-hints';
import { TOUR_EVENT, TOUR_OFFER_KEY, TOUR_TAKEN_KEY, TOUR_WATCHED_KEY } from '../renderer/tour';
import type { PaigeEngagement, PaigeEngagementChannel } from './engagement-channel';

/**
 * CapturePoint's hints and tour flags, kept per person on the server. The renderer reads and writes
 * them in browser storage (document-hints.ts, tour.ts) and Paige leaves those files alone: storage
 * is a mirror. The record is written into it when it arrives (so a shared browser shows each person
 * their own), and what the renderer changes goes back up: a hint closed or Show hints switched (the
 * renderer announces both, HINTS_EVENT), the tour offered or taken (read on the tour's own
 * announcement and when the page is hidden).
 */

/** Set once this browser's own hints and tour flags were carried up to the first person's record. */
export const ENGAGEMENT_ADOPTED_KEY = 'paige.engagement.adopted';

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const sameHints = (record: PaigeEngagement, show: boolean, closed: string[]): boolean =>
  record.hints.show === show &&
  record.hints.closed.length === closed.length &&
  record.hints.closed.every((hint) => closed.includes(hint.id));

/** The record's tour flags in the renderer's storage; throws where the store is blocked. */
function mirrorTour(record: PaigeEngagement, storage: Store | undefined): void {
  const flags: [string, string | null][] = [
    [TOUR_OFFER_KEY, record.tour.offeredAt],
    [TOUR_TAKEN_KEY, record.tour.takenAt],
  ];
  for (const [key, at] of flags)
    if (at) storage?.setItem(key, at);
    else storage?.removeItem(key);
  // A tour taken to its end is offered as "Watch the tour again" wherever this person signs in
  // (CapturePoint 9023888b keeps that flag per installation; leaving part-way stays this browser's).
  if (record.tour.takenAt) storage?.setItem(TOUR_WATCHED_KEY, record.tour.takenAt);
}

/** Writes the record's hints and tour flags into the renderer's storage and tells the hints to look. */
export function mirrorEngagement(record: PaigeEngagement, storage: Store | undefined): void {
  try {
    storage?.setItem(HINTS_CLOSED_KEY, JSON.stringify(record.hints.closed.map((hint) => hint.id)));
    storage?.setItem(SHOW_HINTS_KEY, record.hints.show ? 'on' : 'off');
    mirrorTour(record, storage);
  } catch {
    /* A blocked store: the renderer offers what it would anyway. */
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(HINTS_EVENT));
}

/** The tour flags the renderer set that the record does not have yet. */
function newTourSteps(record: PaigeEngagement, storage: Store | undefined) {
  const read = (key: string) => {
    try {
      return storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  };
  const steps: { step: 'offered' | 'taken'; at: string }[] = [];
  const offered = read(TOUR_OFFER_KEY);
  const taken = read(TOUR_TAKEN_KEY);
  if (offered && !record.tour.offeredAt) steps.push({ step: 'offered', at: offered });
  if (taken && !record.tour.takenAt) steps.push({ step: 'taken', at: taken });
  return steps;
}

/** What the renderer changed in storage, carried up; answers the record as it then stands. */
export async function carryUp(
  record: PaigeEngagement,
  channel: PaigeEngagementChannel,
  storage: Store | undefined,
  adopting = false,
): Promise<PaigeEngagement> {
  let next = record;
  for (const { step, at } of newTourSteps(next, storage))
    next = await channel.tour(step, Number.isNaN(Date.parse(at)) ? undefined : at);
  // CapturePoint d3549569: one Show hints checkbox; checking it again brings every hint back (none closed).
  const local = readHintSettings(storage);
  // Adopting: this browser's own closed hints join the record's, and an "off" here stays off.
  const closed = adopting
    ? [...new Set([...next.hints.closed.map((h) => h.id), ...local.closed])]
    : local.closed;
  const show = adopting ? next.hints.show && local.enabled : local.enabled;
  if (!sameHints(next, show, closed)) next = await channel.hints(show, closed);
  return next;
}

/**
 * Follows the renderer's changes until the returned stop: each one is carried up through `carry`,
 * which keeps the record current. A change that fails is tried again on the next one.
 */
export function followRenderer(carry: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const hidden = () => {
    if (document.visibilityState === 'hidden') carry();
  };
  window.addEventListener(HINTS_EVENT, carry);
  window.addEventListener(TOUR_EVENT, carry);
  document.addEventListener('visibilitychange', hidden);
  return () => {
    window.removeEventListener(HINTS_EVENT, carry);
    window.removeEventListener(TOUR_EVENT, carry);
    document.removeEventListener('visibilitychange', hidden);
  };
}

export function isAdopted(storage: Store | undefined): boolean {
  try {
    return storage?.getItem(ENGAGEMENT_ADOPTED_KEY) === '1';
  } catch {
    return true;
  }
}

export function markAdopted(storage: Store | undefined): void {
  try {
    storage?.setItem(ENGAGEMENT_ADOPTED_KEY, '1');
  } catch {
    /* Adopting again only re-sends what the record already has. */
  }
}
